import { memo, useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import PropTypes from 'prop-types';

import { useIsDesktopLg } from '#/hooks/useMatchMedia';
import useTokenExpiryCheck from '#/hooks/useTokenExpiryCheck';
import Breadcrumb from '#/modules/commonComponents/Breadcrumb';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '#/modules/commonComponents/sheet';
import Aside from '#/modules/infra/layout/Aside';
import AsideNavContent from '#/modules/infra/layout/AsideNavContent';
import Nav from '#/modules/infra/layout/Nav';

/**
 * Admin 外殼 — Aside + Nav + Breadcrumb + Outlet。
 * 桌面／手機以 matchMedia(lg) 互斥：desktop 只掛 rail、mobile 只掛 Sheet。
 * mobile 一律掛 SheetContent，由 Radix Presence 播完 exit 再卸。
 *
 * @see docs/UI_LAYOUT.md
 */
const AdminLayout = ({ children }) => {
  const isDesktop = useIsDesktopLg();
  const [toggleExpanded, setToggleExpanded] = useState(false);
  const [mobileAsideOpenForPath, setMobileAsideOpenForPath] = useState(null);
  const location = useLocation();

  // 切回桌面時清掉手機開啟標記（render 期調整，避免殘留下次變窄又自動開）
  if (isDesktop && mobileAsideOpenForPath !== null) {
    setMobileAsideOpenForPath(null);
  }

  const mobileAsideOpen = !isDesktop
    && mobileAsideOpenForPath === location.pathname;
  useTokenExpiryCheck();

  const setToggleExpand = useCallback((next) => {
    setToggleExpanded(next);
  }, []);

  const setMobileAsideOpen = useCallback((next) => {
    setMobileAsideOpenForPath(next ? location.pathname : null);
  }, [location.pathname]);

  const navProps = useMemo(
    () => ({
      isDesktop,
      toggleExpanded,
      setToggleExpand,
      mobileAsideOpen,
      setMobileAsideOpen,
    }),
    [
      isDesktop,
      toggleExpanded,
      setToggleExpand,
      mobileAsideOpen,
      setMobileAsideOpen,
    ],
  );

  return (
    <div className="admin-layout flex min-h-screen w-full">
      {isDesktop ? (
        <Aside toggleExpanded={toggleExpanded} />
      ) : (
        <Sheet open={mobileAsideOpen} onOpenChange={setMobileAsideOpen}>
          <SheetContent
            side="left"
            showCloseButton={false}
            className="
              w-full max-w-65 gap-0 border-0 p-0 shadow-none
              sm:max-w-65 data-[side=left]:border-r-0
            "
          >
            <SheetTitle className="sr-only">導覽選單</SheetTitle>
            <SheetDescription className="sr-only">系統功能選單</SheetDescription>
            <aside className="aside aside--sheet">
              <AsideNavContent />
            </aside>
          </SheetContent>
        </Sheet>
      )}
      <div className="admin-layout__main flex min-h-0 min-w-0 flex-1 flex-col">
        <Nav {...navProps} />
        <main className="admin-layout__content relative min-h-0 flex-1 overflow-hidden p-5">
          <div className="shrink-0">
            <Breadcrumb />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {children ?? <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
};

AdminLayout.propTypes = {
  children: PropTypes.node,
};

export default memo(AdminLayout);
